// src/components/Header/Header.js

import React, { useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Box,
  Typography,
  Popover,
  Divider,
  Button,
  Grid,
  ListItemButton,
  ListItemText,
  Grow,
  ListItemIcon,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Close as CloseIcon,
  AccountCircle as AccountCircleIcon,
  HelpOutline as HelpIcon,
  Brightness4 as Brightness4Icon, 
  Brightness7 as Brightness7Icon,
  CalendarMonth as CalendarIcon,
  MailOutline as MailIcon,
  VideoCall as MeetIcon,
  Notifications as NotificationsIcon,
  Public as PublicIcon,
  NewReleases as NewReleasesIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { useOrganization } from "../../shared/OrganizationContext";
import { useTour } from '../../shared/TourContext';
import useAuth from '../../hooks/useAuth';
import { useTheme } from '@mui/material/styles';
import { useThemeToggle } from '../../shared/ThemeContext';
import ModalContext from '../../shared/ModalContext/ModalContext';
import { useQuery } from '@tanstack/react-query';
import { listCalendarEvents } from '../../ctapi/calendarService';
import { isSameDay, parseISO } from 'date-fns';

const Header = ({ onWhatsNewClick }) => {
  const { user, signOut } = useAuth();
  const { organization, setOrganization } = useOrganization();
  const { setRunTour } = useTour();
  const theme = useTheme();
  const { toggleTheme } = useThemeToggle(); 
  
  const [accountAnchor, setAccountAnchor] = React.useState(null);
  const [appsAnchor, setAppsAnchor] = React.useState(null);
  const [helpAnchor, setHelpAnchor] = React.useState(null);
  const [notificationsAnchor, setNotificationsAnchor] = React.useState(null);
  const { openModal } = React.useContext(ModalContext);
  
  const navigate = useNavigate();

  const { data: eventsData } = useQuery({
      queryKey: ['calendarEvents'],
      queryFn: () => listCalendarEvents().catch(() => []),
      staleTime: 5 * 60 * 1000,
  });

  // This logic is now consistent with the dashboard's Upcoming Meetings component
  const meetings = useMemo(() => {
    if (!eventsData) return [];
    return eventsData.map(event => {
        const summary = event.summary || '';
        const candidateMatch = summary.match(/Interview:\s*(.*?)\s*(for|$)/);
        const candidate = candidateMatch && candidateMatch[1] ? candidateMatch[1].trim() : summary;
        
        return {
            ...event, // Pass the full event object
            id: event.id,
            date: event.start.dateTime || event.start.date, // Handle all-day events
            time: event.start.dateTime ? new Date(event.start.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'All-day',
            title: summary,
            candidate: candidate,
        };
    });
  }, [eventsData]);


  const todaysMeetingsCount = useMemo(() => {
    if (!eventsData) return 0;
    const today = new Date();
    return eventsData.filter(event => isSameDay(parseISO(event.start.dateTime || event.start.date), today)).length;
  }, [eventsData]);
  
  if (!user) {
    return null;
  }

  const logoSrc = theme.palette.mode === 'dark' 
        ? '/ct_logo.png' 
        : '/ct_logo_black.png';

  const handleLogout = () => {
    signOut();
    setAccountAnchor(null);
  };
  
  const switchOrg = (org) => {
    setOrganization(org);
    setAccountAnchor(null);
    navigate('/');
  };

  const launcherItems = [
    { 
      icon: <AccountCircleIcon />, 
      label: 'Account', 
      onClick: (navigate, setAppsAnchor) => {
        navigate('/settings', { state: { defaultSection: 'account' } });
        setAppsAnchor(null);
      }
    },
    { icon: <PublicIcon />, label: 'Account', url: '' },
    { icon: <CalendarIcon />, label: 'Calendar', url: 'https:/calendar.google.com/' },
    { icon: <MailIcon />, label: 'Gmail', url: 'https://mail.google.com/' },
    { icon: <MeetIcon />, label: 'Meet', url: 'https://meet.google.com/' },
  ];

  const AccountPopover = (
    <Popover
      open={Boolean(accountAnchor)}
      anchorEl={accountAnchor}
      onClose={() => setAccountAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Grow}
      PaperProps={{
        sx: {
          bgcolor: 'background.paper', 
          borderRadius: 4,
          width: 360,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</Typography>
        <IconButton size="small" aria-label="Close account menu" onClick={() => setAccountAnchor(null)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3 }}>
        <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '2.5rem', mb: 1.5 }}>
          {user.email[0].toUpperCase()}
        </Avatar>
        <Typography variant="h5">Hi, {user.firstName ?? 'there'}!</Typography>
      </Box>
      <Box sx={{ px: 2, mb: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          component={Link}
          to="/settings"
          state={{ defaultSection: 'account' }}
          onClick={() => setAccountAnchor(null)}
          sx={{ borderRadius: '20px', borderColor: 'divider', color: 'text.primary', textTransform: 'none', py: 1 }}
        >
          Edit Account
        </Button>
      </Box>
      <Box sx={{ px: 2, mb: 2 }}>
        
        {/* 
        <Button
          fullWidth
          variant="outlined"
          component={Link}
          to="/settings"
          state={{ defaultSection: 'organization' }}
          onClick={() => setAccountAnchor(null)}
          sx={{ borderRadius: '20px', borderColor: 'divider', color: 'text.primary', textTransform: 'none', py: 1 }}
        >
          Organization Settings
        </Button>
        */}
      </Box>
      <Divider />
      <Box sx={{ maxHeight: 180, overflowY: 'auto', my: 1, mx: 1.5 }}>
        <Typography variant="caption" display="block" sx={{ px: 1, pb: 1, color: 'text.secondary', fontWeight: 500 }}>
          SWITCH ORGANIZATION
        </Typography>
        {user?.roles?.map(({ organization: org }) => (
          <ListItemButton
            key={org.uuid}
            selected={org.uuid === organization?.uuid} 
            onClick={() => switchOrg(org)}
            sx={{ 
              borderRadius: 2, 
              mb: 0.5, 
              '&.Mui-selected': { 
                bgcolor: 'action.selected', 
                '&:hover': { bgcolor: 'action.selected' }
              }
            }}
          >
            <ListItemText
              primary={org.descriptiveName}
              primaryTypographyProps={{ 
                fontWeight: 500, 
                color: org.uuid === organization?.uuid ? 'primary.main' : 'text.primary' 
              }} 
            />
          </ListItemButton>
        ))}
      </Box>
      <Divider />
      <ListItemButton onClick={toggleTheme} sx={{ py: 1.5, px: 2 }}>
        <ListItemIcon sx={{ minWidth: 40 }}>
          {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </ListItemIcon>
        <ListItemText primary="Appearance" />
        <Typography variant="body2" color="text.secondary">
          {theme.palette.mode === 'dark' ? 'Dark' : 'Light'}
        </Typography>
      </ListItemButton>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleLogout}
          sx={{ borderRadius: '20px', borderColor: 'divider', color: 'text.primary', textTransform: 'none', py: 1 }}
        >
          Sign Out
        </Button>
      </Box>
    </Popover>
  );

  const AppsPopover = (
    <Popover
      open={Boolean(appsAnchor)}
      anchorEl={appsAnchor}
      onClose={() => setAppsAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Grow}
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          borderRadius: 4,
          p: 2,
          width: 320,
          maxHeight: { xs: '70vh', sm: 480 },
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Grid container spacing={1}>
        {launcherItems.map(({ icon, label, url, onClick }) => (
          <Grid key={label} item xs={4} textAlign="center">
            <IconButton
              aria-label={label}
              onClick={() => {
                if (url) {
                  window.open(url, '_blank');
                  setAppsAnchor(null); 
                } else if (onClick) {
                  onClick(navigate, setAppsAnchor);
                }
              }}
              sx={{ flexDirection: 'column', p: 1.5, borderRadius: 2, width: '100%' }}
            >
              {React.cloneElement(icon, { sx: { fontSize: 32, mb: 0.5 } })}
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {label}
              </Typography>
            </IconButton>
          </Grid>
        ))}
      </Grid>
    </Popover>
  );
  
  const HelpPopover = (
    <Popover
        open={Boolean(helpAnchor)}
        anchorEl={helpAnchor}
        onClose={() => setHelpAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
            sx: {
                width: 200,
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
            },
        }}
    >
        <ListItemButton onClick={() => {
            setRunTour(true);
            setHelpAnchor(null);
        }}>
            <ListItemText primary="Take Tour" />
        </ListItemButton>
        <ListItemButton component={Link} to="/help" onClick={() => setHelpAnchor(null)}>
            <ListItemText primary="Help Center" />
        </ListItemButton>
    </Popover>
  );

  return (
    <>
      <AppBar 
        elevation={0} 
        sx={{ 
          bgcolor: 'background.paper', 
          color: 'text.primary', 
          borderBottom: '1px solid', 
          borderColor: 'divider' 
          
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
          <IconButton
            component={Link}
            src={logoSrc}
            to="/"
            edge="start"
            aria-label="Home"
            sx={{ p: 0, mr: { xs: 1, sm: 2 } }}
          >
            <img src={logoSrc} alt="Candidate Tools Logo" style={{ height: '40px', width: 'auto' }} />
          </IconButton>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            
            {/*
            <Tooltip title="What's New">
                <IconButton color="inherit" aria-label="what's new" onClick={onWhatsNewClick}>
                    <NewReleasesIcon />
                </IconButton>
            </Tooltip>
            */}

            <Tooltip title="Calendar">
                <IconButton color="inherit" aria-label="calendar" onClick={() => openModal('CALENDAR_MODAL', { events: meetings })}>
                  <Badge badgeContent={todaysMeetingsCount} color="error">
                    <CalendarIcon />
                  </Badge>
                </IconButton>
            </Tooltip>
            <Tooltip title="Help">
              <IconButton color="inherit" aria-label="help" onClick={(e) => setHelpAnchor(e.currentTarget)}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
            {/* Temporarily disabled notifications icon */}
            <Tooltip title="Apps">
              <IconButton color="inherit" aria-label="Apps launcher" onClick={(e) => setAppsAnchor(e.currentTarget)}>
                <AppsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Account">
              <IconButton aria-label="Account settings" onClick={(e) => setAccountAnchor(e.currentTarget)}>
                <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, color: 'primary.contrastText', fontSize: '0.875rem' }}>
                  {user?.email ? user.email[0].toUpperCase() : <AccountCircleIcon />}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {AccountPopover}
      {AppsPopover}
      {HelpPopover}
    </>
  );
};

export default Header;